import React, { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import ResolvePopup from 'components/ResolvePopup';
import { Button } from 'ncoded-component-library';
import SmileIcon from 'icons/Smile.icon';
import { REACTION_TYPE, ReactionType } from 'models/Reactions';
import LikeIcon from 'icons/Like.icon';
import DislikeIcon from 'icons/Dislike.icon';
import CircledQuestionmarkIcon from 'icons/CircledQuestionmark.icon';
import useHandleClickOutside from 'hooks/useHandleClickOutside';
import {
  isQuestionMarkReaction,
  isThumbsDownReaction,
  isThumbsUpReaction,
} from 'utils';

import './ReactionPopup.styles.scss';

type ReactionPopupProps = {
  className?: string;
  activeReaction?: ReactionType;
  onReact?: (reaction: ReactionType) => void;
};

const ReactionPopup: React.FC<ReactionPopupProps> = (props) => {
  const { className, activeReaction, onReact } = props;

  const [isOpen, setIsOpen] = useState(false);

  const classes = classNames('reaction-popup', className);

  const closePopup = useCallback(() => setIsOpen(false), []);

  const { contentRef } = useHandleClickOutside(closePopup);

  const handleOnReact = useCallback(
    (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
      reaction: ReactionType,
    ) => {
      e.stopPropagation();
      closePopup();
      onReact?.(reaction);
    },
    [closePopup, onReact],
  );

  const popupContent = useMemo(
    () => (
      <div className="reaction-popup__content__actions">
        <Button
          icon={LikeIcon}
          variant="icon"
          className={classNames('like', {
            active: isThumbsUpReaction(activeReaction),
          })}
          onClick={(e) => handleOnReact(e, REACTION_TYPE.THUMBS_UP)}
        />
        <Button
          icon={DislikeIcon}
          variant="icon"
          className={classNames('dislike', {
            active: isThumbsDownReaction(activeReaction),
          })}
          onClick={(e) => handleOnReact(e, REACTION_TYPE.THUMBS_DOWN)}
        />
        <Button
          icon={CircledQuestionmarkIcon}
          variant="icon"
          className={classNames('question-mark', {
            active: isQuestionMarkReaction(activeReaction),
          })}
          onClick={(e) => handleOnReact(e, REACTION_TYPE.QUESTION_MARK)}
        />
      </div>
    ),
    [activeReaction, handleOnReact],
  );

  return (
    <ResolvePopup
      open={isOpen}
      className={classes}
      popupContent={popupContent}
      setRef={contentRef}
      contentClassName="reaction-popup__content"
    >
      <Button
        icon={SmileIcon}
        variant="icon"
        className="reaction-button"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((old) => !old);
        }}
      />
    </ResolvePopup>
  );
};

export default ReactionPopup;
